import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { imgSrc } from "@/lib/img";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, BadgeCheck, Quote, Heart } from "lucide-react";

// Data-driven landing sections extracted from pages/Landing.jsx (Phase 15
// component split).
// Phase 16 item 1 (2026-07): both sections are curation-gated — "Trusted
// causes" only lists verified orgs with ≥1 open opportunity (the API filters)
// and hides entirely when empty; stories are admin-featured only and the
// section hides below 3.

// Verified causes with open roles, ranked by hours mobilised. Hidden when empty.
export function FeaturedOrgsSection({ featured }) {
  // Landing polish (2026-07): a "trusted causes" wall with 1-2 cards looks
  // thinner than nothing — mirror StoriesSection and only render at 3+.
  if (featured.length < 3) return null;
  return (
        <section className="max-w-7xl mx-auto px-6 py-24" data-testid="featured-orgs-section">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold inline-flex items-center gap-2">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified causes
                </div>
                <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Trusted causes making real impact</h2>
                <p className="text-forest/70 mt-4 max-w-xl text-lg">Independently verified organisations, ranked by the volunteer hours they've mobilised.</p>
              </div>
              <Link to="/opportunities" className="text-sm font-medium text-forest hover:text-forest-hover inline-flex items-center gap-1.5">
                Browse all opportunities <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {featured.map((o, i) => (
              <Reveal key={o.user_id} delay={i * 0.1}>
                <Link
                  to={`/o/${o.user_id}`}
                  data-testid={`featured-org-${o.user_id}`}
                  className="group block civic-glass rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10 h-full"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-3xl text-forest/20 w-8">{i + 1}</span>
                      <div className="font-display text-xl text-forest leading-tight">{o.org_name}</div>
                    </div>
                    <BadgeCheck className="w-5 h-5 text-forest shrink-0" />
                  </div>
                  {o.location && <div className="text-xs text-neutral-500 mt-3">{o.location}</div>}
                  <div className="flex items-baseline gap-5 mt-5">
                    <div>
                      <div className="font-display text-3xl text-forest">{o.hours}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-0.5">hours</div>
                    </div>
                    <div>
                      <div className="font-display text-3xl text-forest">{o.volunteers}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-0.5">volunteers</div>
                    </div>
                    {o.open_opportunities > 0 && (
                      <div className="ml-auto self-center">
                        <Badge className="bg-terracotta/10 text-terracotta border-none rounded-full">{o.open_opportunities} open</Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-5">
                    {(o.values || []).slice(0, 3).map(v => (
                      <Badge key={v} variant="outline" className="rounded-full text-[11px] capitalize border-forest/20 text-forest">{v}</Badge>
                    ))}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
  );
}

// Admin-featured stories only; hidden entirely below 3 (Phase 16 item 1).
export function StoriesSection({ stories }) {
  if (stories.length < 3) return null;
  return (
        <section className="max-w-7xl mx-auto px-6 py-24" data-testid="landing-stories-section">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold inline-flex items-center gap-2">
                  <Quote className="w-3.5 h-3.5" /> Voices from the field
                </div>
                <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Stories from real volunteers</h2>
              </div>
              <Link to="/stories" className="text-sm font-medium text-forest hover:text-forest-hover inline-flex items-center gap-1.5">
                Read all stories <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {stories.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}>
                <Link
                  to="/stories"
                  data-testid={`landing-story-${s.id}`}
                  className="group relative block rounded-3xl overflow-hidden h-full min-h-[340px] border border-forest/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10"
                >
                  {s.image ? (
                    <img src={imgSrc(s.image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="absolute inset-0 bg-forest" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-transparent" />
                  <div className="relative flex flex-col justify-end h-full p-7 text-white">
                    <p className="text-sm leading-relaxed line-clamp-4 text-white/95">"{s.text}"</p>
                    <div className="flex items-center gap-2.5 mt-5">
                      <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-sm font-display">{s.user_name?.[0]}</div>
                      <div>
                        <div className="text-sm font-medium">{s.user_name}</div>
                        {/* non-impact stories have no hours/org — never render "h with " */}
                        <div className="text-[11px] text-white/70">
                          {s.hours ? `${s.hours}h with ${s.org_name}` :
                            s.story_type === "gathering" ? "Community gathering" : "Neighbourhood moment"}
                        </div>
                      </div>
                      <span className="ml-auto inline-flex items-center gap-1 text-xs text-white/80">
                        <Heart className="w-3.5 h-3.5" /> {s.likes?.length || 0}
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
  );
}
